import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MathHelper } from "@/Common/Helpers/MathHelper";
import { Guid } from "@/Common/Models/Guid";
import { Height } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { BloodPressure } from "../MedicalSummary/BloodPressure";
import { MedicalItemVitalSign } from "../MedicalSummary/MedicalItemVitalSign";
import { VitalSign } from "../VitalSigns/VitalSign";
import { ObstetricalFileMedicalSummary } from "./ObstetricalFileMedicalSummary";

class ObstetricalFileVitalSign extends VitalSign {
    private _vitalSign: MedicalItemVitalSign;

    public medicalItemId: string;
    public emrIsInvalid: boolean;

    public constructor(vitalSign?: MedicalItemVitalSign, weightUnit: WeightUnit = WeightUnit.Kilogram, heightUnit: HeightUnit = HeightUnit.Meter) {
        super();
        this.updateValueFromMedicalItemVitalSign(vitalSign, weightUnit, heightUnit);
    }

    public updateValueFromMedicalItemVitalSign(
        vitalSign: MedicalItemVitalSign,
        weightUnit: WeightUnit = WeightUnit.Kilogram,
        heightUnit: HeightUnit = HeightUnit.Meter
    ): void {
        this._vitalSign = vitalSign && new MedicalItemVitalSign(vitalSign);

        this._heightUnit = heightUnit;
        this._height = this._vitalSign?.getHeightIn(heightUnit);
        if (this._height)
            this._height = MathHelper.roundToDecimals(this._height);

        this._weightUnit = weightUnit;
        this._weight = this._vitalSign?.getWeightIn(weightUnit);
        if (this._weight)
            this._weight = MathHelper.roundToDecimals(this._weight);

        this.bloodPressure = new BloodPressure({ systolic: this._vitalSign?.BPSys, diastolic: this._vitalSign?.BPDia });
        this.medicalItemId = this._vitalSign?.MedicalItemId;
        this.emrIsInvalid = this._vitalSign?.EMRTemplateIsInvalid;
        this.bodyMassIndex = this._vitalSign?.MassBodyIndex;
        this.bodySurface = this._vitalSign?.BodySurface;
        this.heartRate = this._vitalSign?.HeartRate;
    }

    public static createObstetricalFileVitalSign(
        options: {
            obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary,
            emrTemplateId?: string,
            consultationDate: Date,
            obstetricalFileContributionDate: Date,
            skipLoadLastHour?: boolean,
            weightUnit?: WeightUnit,
            heightUnit?: HeightUnit
        }
    ): ObstetricalFileVitalSign {
        let vitalSign = options.obstetricalFileMedicalSummary?.getVitalSignBy(options.emrTemplateId, options.consultationDate);

        if (!vitalSign && !options.skipLoadLastHour)
            vitalSign = options.obstetricalFileMedicalSummary?.getVitalSignFromLastHour(options.consultationDate, options.obstetricalFileContributionDate);

        return new ObstetricalFileVitalSign(
            vitalSign,
            options.weightUnit ?? WeightUnit.Kilogram,
            options.heightUnit ?? HeightUnit.Meter
        );
    }

    public get emrTemplateId(): string {
        return this._vitalSign?.EMRTemplateId;
    }

    public isUndelyingMedicalItemEmpty(): boolean {
        return (
            !this._vitalSign.BodySurface
            && !this._vitalSign.MassBodyIndex
            && !this._vitalSign.SerumCreatinine
            && !this._vitalSign.HeartRate
            && !this._vitalSign.O2Sat
            && !this._vitalSign.HeadCircumference
            && !this._vitalSign.WaistCircumference
            && !this._vitalSign.BPDiaStanding
            && !this._vitalSign.BPSysStanding
            && !this._vitalSign.BPDiaLaid
            && !this._vitalSign.BPSysLaid
            && !this._vitalSign.BloodGlucose
            && !this._vitalSign.PainScaleValue
            && !this._vitalSign.Note
            && !this._vitalSign.IsCommitted
            && !this._vitalSign.Temperature
        );
    }

    public hasValue(): boolean {
        return !!this.height
            || !!this.weight
            || this.bloodPressure?.hasValue()
            || !!this.heartRate;
    }

    public hasEnoughDataToSaveMedicalItem(): boolean {
        return this.hasValue() || !!this.medicalItemId;
    }

    public getLatestVitalSignMedicalItemId(): string {
        return this._vitalSign?.MedicalItemId;
    }

    public toMedicalItemVitalSignRequest(encounterId: Guid, encounterConsultationDate: Date): MedicalItemVitalSign {
        if (!this.hasEnoughDataToSaveMedicalItem())
            return null;

        let vitalSign = new MedicalItemVitalSign(this._vitalSign);

        if (vitalSign.RelatedEncounterId !== encounterId) {
            vitalSign.Id = GuidHelper.Empty();
            vitalSign.MedicalItemId = GuidHelper.Empty();
            vitalSign.RelatedEncounterId = encounterId;
            vitalSign.ConsultationDate = encounterConsultationDate;
        }

        if (this.weight && !vitalSign.WeightUnitLookupId) {
            vitalSign.WeightUnitLookupId = WeightUnitConverter.toLookup(this._weightUnit)?.LookupID;
        }

        if (this.height && !vitalSign.HeightUnitLookupId) {
            vitalSign.HeightUnitLookupId = this.getMedicalItemHeightUnitLookupId();
        }

        vitalSign.WeightUnitLookupValue = cacheStore.GetLocalizedValueByLookupId(vitalSign.WeightUnitLookupId);
        vitalSign.Weight = vitalSign.WeightUnitLookupValue === "lb" ? this.getWeightIn(WeightUnit.Pound) : this.weightInKgs;

        vitalSign.HeightUnitLookupValue = cacheStore.GetLocalizedValueByLookupId(vitalSign.HeightUnitLookupId);
        vitalSign.Height = vitalSign.HeightUnitLookupValue === "in"
            ? this.getHeightIn(HeightUnit.Inch)
            : this.getHeightIn(HeightUnit.Centimeter);

        vitalSign.BPDia = this.bloodPressure?.diastolic;
        vitalSign.BPSys = this.bloodPressure?.systolic;
        vitalSign.MassBodyIndex = this.bodyMassIndex;
        vitalSign.BodySurface = this.bodySurface;
        vitalSign.HeartRate = this.heartRate;

        return vitalSign;
    }

    /**
     * Gets the heightUnitLookupId for the medical item.
     * Because medical item uses centimeter and inch, obstretical file meter is set as centimeter
     * And the meter value is converted to centimeter
     */
    private getMedicalItemHeightUnitLookupId(): string {
        switch (this._heightUnit) {
            case HeightUnit.Centimeter:
            case HeightUnit.Meter:
                return cacheStore.FindLookupByLookupCode(Height.Centimeter, Height.Code).LookupID;
            case HeightUnit.Inch:
                return cacheStore.FindLookupByLookupCode(Height.Inch, Height.Code).LookupID;
        }
    }
}

export { ObstetricalFileVitalSign };
